import { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/styles.utils";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import { Icon } from "@/components/icons/Icon";
import type { InspectionState } from "../InspectionsLists.d";

import {
  ACTIVE_SORTERS,
  CLOSED_SORTERS,
} from "@/assets/constants/selectsLists";
import InputSelect from "@/components/input-select/InputSelect";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import TerciaryButton from "@/components/buttons/terciary-button/TerciaryButton";
import { getIsFiltered } from "../utils/inspections.utils";

export interface InspectionsMobileSorterProps {
  tableType: InspectionState;
  lastFiltered: string;
  lastSort: { sort: string; direction: "asc" | "desc" } | null;
  updateLastSort: (
    lastSort: { sort: string; direction: "asc" | "desc" } | null,
  ) => void;
  searchInspections: (params: string) => void;
  getNoSortedInspections: () => void;
  close: () => void;
}

const InspectionsMobileSorter = ({
  tableType,
  lastFiltered,
  lastSort,
  updateLastSort,
  searchInspections,
  getNoSortedInspections,
  close,
}: InspectionsMobileSorterProps) => {
  const translate = useTranslate();

  const [selectedSorter, setSelectedSorter] = useState<string>("");
  const [direction, setDirection] = useState<"asc" | "desc">("asc");

  const sortersOptions = useMemo(() => {
    const _createOptions = (optionsList: string[]) => {
      return optionsList.map((sorter: string) => {
        return {
          value: sorter,
          label: translate(sorter),
        };
      });
    };

    if (tableType === "active") return _createOptions(ACTIVE_SORTERS);
    return _createOptions(CLOSED_SORTERS);
  }, [tableType, translate]);

  useEffect(() => {
    if (sortersOptions && !lastSort?.sort)
      setSelectedSorter(sortersOptions[0].value);
    if (lastSort?.sort && lastSort?.direction) {
      setSelectedSorter(lastSort.sort);
      setDirection(lastSort.direction);
    } else {
      setSelectedSorter(sortersOptions[0].value);
      setDirection("asc");
    }
  }, []);

  const changeDirection = useCallback(() => {
    if (direction === "asc") setDirection("desc");
    else setDirection("asc");
  }, [direction]);

  const sortList = useCallback(() => {
    let sorter = "";
    switch (selectedSorter) {
      case "startDate":
        sorter = "createdAt";
        break;
      case "engineType":
        sorter = "engineName";
        break;
      case "snStand":
        sorter = "nsStand";
        break;
      case "esnEnter":
        sorter = "esnIn";
        break;
      case "esnExit":
        sorter = "esnOut";
        break;
      case "phase":
        sorter = "phaseName";
        break;
      default:
        sorter = selectedSorter;
        break;
    }
    const sortParams = `&orderBy=${sorter}&order=${direction}&page=`;
    const noSortedLastFiltered = lastFiltered.includes("&orderBy=")
      ? lastFiltered.split("&orderBy=")[0]
      : lastFiltered;
    let filterParams = noSortedLastFiltered.includes("inspections?")
      ? noSortedLastFiltered.split("inspections?")[1].replace("page=", "")
      : "";
    if (!filterParams.length)
      filterParams = `status=${
        tableType === "active" ? "IN_PROGRESS" : " CLOSED"
      }&size=20`;

    const params = `?${filterParams}${sortParams}`.replaceAll("&&", "&");
    updateLastSort({
      sort: selectedSorter,
      direction: direction,
    });
    searchInspections(params);
    close();
  }, [
    selectedSorter,
    direction,
    lastFiltered,
    searchInspections,
    tableType,
    updateLastSort,
    close,
  ]);

  const resetSorting = useCallback(() => {
    const isFiltered = getIsFiltered(lastFiltered);
    if (!isFiltered) {
      updateLastSort(null);
      if (selectedSorter !== "startDate") setSelectedSorter("startDate");
      if (direction === "desc") setDirection("asc");
      getNoSortedInspections();
    } else {
      if (selectedSorter !== "startDate") setSelectedSorter("startDate");
      if (direction === "desc") setDirection("asc");
      const sortParams = `&page=`;
      const noSortedLastFiltered = lastFiltered.includes("&orderBy=")
        ? lastFiltered.split("&orderBy=")[0]
        : lastFiltered;
      let filterParams = noSortedLastFiltered.includes("inspections?")
        ? noSortedLastFiltered.split("inspections?")[1].replace("page=", "")
        : "";
      if (!filterParams.length)
        filterParams = `status=${
          tableType === "active" ? "IN_PROGRESS" : " CLOSED"
        }&size=20`;

      const params = `?${filterParams}${sortParams}`.replaceAll("&&", "&");
      updateLastSort(null);
      searchInspections(params);
    }
  }, [
    selectedSorter,
    direction,
    getNoSortedInspections,
    lastFiltered,
    searchInspections,
    tableType,
    updateLastSort,
  ]);

  return (
    <div className="fixed top-0 left-0 w-[100vw] h-[100vh] bg-ib-gray-lightest-2 z-[1001]">
      <div className="w-full h-14 bg-ib-white p-4 flex flex-row justify-between items-center border-b border-b-ib-gray-borders mb-3">
        <div className="text-[16px] leading[24px] ib-gray-darkest">
          {translate("sort")}
        </div>
        <button onClick={close}>
          <Icon name="close" className="size-6 text-ib-red-main" />
        </button>
      </div>
      <div className="px-4">
        <div className="w-full mb-6">
          <InputSelect
            value={selectedSorter}
            onChange={(data) => setSelectedSorter(data)}
            placeholder=""
            label={translate("sortBy")}
            options={sortersOptions}
            id={"sorters"}
          />
        </div>
        <div className="w-full flex flex-row gap-2 border border-ib-gray-borders p-1 rounded-round">
          <button
            disabled={direction === "asc"}
            onClick={changeDirection}
            className={cn(
              "grow h-[34px] text-ib-main rounded-round",
              direction === "asc"
                ? "bg-ib-red-main font-ib-bold text-ib-white"
                : "bg-ib-white text-ib-gray-main",
            )}
          >
            {translate("ascending")}
          </button>
          <button
            disabled={direction === "desc"}
            onClick={changeDirection}
            className={cn(
              "grow h-[34px] text-ib-main rounded-round",
              direction === "desc"
                ? "bg-ib-red-main font-ib-bold text-ib-white"
                : "bg-ib-white text-ib-gray-main",
            )}
          >
            {translate("descending")}
          </button>
        </div>
      </div>
      <div className="fixed w-full bottom-0 bg-ib-white border-t border-t-ib-gray-borders px-[19px] py-4 flex flex-col gap-y-4">
        <PrimaryButton
          colour="red"
          label={translate("applySorting")}
          onClick={sortList}
          icon="okCheck"
        />
        <TerciaryButton
          colour="black"
          label={translate("resertSorting")}
          onClick={resetSorting}
          icon="rotateArrows"
        />
      </div>
    </div>
  );
};
export default InspectionsMobileSorter;
